import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Container, Grid, useMediaQuery, useTheme, CircularProgress } from "@mui/material";
import { gsap } from "gsap";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css"; // Import styles for Lightbox
import { TextPlugin } from "gsap/TextPlugin";


import { 
  backsidebackgrounddesktop, 
  backsidebackgroundmobile, 
  connectedmap, 
  gardientline, 
  timeline 
} from "../../../assets";

// Connectivity Data
const connectivityData = [
  { category: "Public Transport", items: ["10 mins - Chandanagar MMTS Station", "15 mins - Miyapur Metro Station", "20 mins - JNTU Metro Station"] },
  { category: "Hospitals", items: ["9 mins - Pranaam Hospital", "20 mins - AIG Hospital", "20 mins - Yashoda Hospital"] },
  { category: "Workplaces", items: ["10 mins - IBM", "10 mins - Dr. Reddy’s Laboratories", "20 mins - HITECH City", "25 mins - Financial District"] },
  { category: "Education Institutes", items: ["5 mins - Meru International School", "12 mins - JNTU", "20 mins - Delhi Public School", "31 mins - Cornerstone International"] },
  { category: "Shopping & Entertainment", items: ["5 mins - Chennai Shopping Mall", "5 mins - GSM Mall", "15 mins - Lulu Mall", "30 mins - IKEA"] }
];


gsap.registerPlugin(TextPlugin);


const destinationLat = 17.509546064259638;
const destinationLng = 78.33811456687631;


const openGoogleMaps = () => {
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destinationLat},${destinationLng}&travelmode=driving`;
  window.open(mapsUrl, "_blank");
};

const images = [connectedmap, connectedmap, connectedmap];

const ConnectedLiving: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Lightbox State
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const helperRef = useRef(null);
  const textRef1 = useRef(null);

  // Refs for GSAP animations
  const textRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLDivElement | null>(null);
  const iconsRef = useRef<HTMLDivElement | null>(null);

  // GSAP Animations
  useEffect(() => {
    gsap.fromTo(textRef.current, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 1, ease: "power2.out" });
    gsap.fromTo(imageRef.current, { opacity: 0, scale: 0.9 }, { opacity: 1, scale: 1, duration: 1.2, ease: "power2.out", delay: 0.3 });
    gsap.fromTo(iconsRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 1, ease: "power2.out", delay: 0.5 });
  }, []);

  // Preload Image
  useEffect(() => {
    const img = new Image();
    img.src = connectedmap;
    img.onload = () => setIsLoading(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

    useEffect(() => {
      gsap.fromTo(
        helperRef.current,
        { opacity: 0, y: -10 },
        { opacity: 1, y: 0, duration: 1, repeat: -1, yoyo: true, ease: "power1.inOut" }
      );
    }, []);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "auto",
        backgroundImage: `url(${isMobile ? backsidebackgroundmobile : backsidebackgrounddesktop})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        px: { xs: 0, md: 3 },
      }}
    >
      <Box sx={{ position: "relative", width: "100%", color: "white", py: 1, textAlign: "center" }}>
        <Container maxWidth="xl" ref={textRef} sx={{px: 0}}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: "600",
                fontSize: "28px",
                background: "linear-gradient(90deg, #bf742c, #fff8da, #c58038)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                display: "inline-block",
                mt: 1.4,
              }}
            >
              Connected Living
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", my: 1, width: "100%" }}>
                               <img src={gardientline} alt="2.5 BHK luxury apartment in Hyderabad by Kolla Group" style={{ width: "100%", maxWidth: "900px", marginRight: 0 }} />
                             </Box>
            </Box>


          {/* Content Section */}
          <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "center", alignItems: "center", mt: isMobile ? 3: -4, width: "100%" }}>
            {/* Image Section */}
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Box ref={imageRef} sx={{ position: "relative", width: "100%", height: { xs: "200px", md: "440px" }, display: "flex", justifyContent: "center", alignItems: "center", borderRadius: 2, mt: 4 }}>
              <Box sx={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                {isLoading ? (
                  <CircularProgress sx={{ color: "#bf742c" }} />
                ) : (
                  <Box
                    component="img"
                    src={connectedmap}
                    alt="2.5 BHK luxury apartment in Hyderabad by Kolla Group"
                    onClick={openGoogleMaps}
                    sx={{ width: "auto", height: { xs: "250px", md: "400px" }, borderRadius: 2, cursor: "pointer" }}
                  />
                )}
                
                {/* Helper Text */}
                <Typography
                  ref={helperRef}
                  onClick={openGoogleMaps}
                  sx={{
                    position: "absolute",
                    bottom: isMobile ? "36%": "25%",
                    transform: "translateY(-50%)",
                    background: "rgba(0,0,0,0.5)",
                    padding: "6px 12px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px", 
                    fontWeight: "bold",
                    boxShadow: "0px 4px 6px rgba(0,0,0,0.2)",
                  }}
                >
                  👉 Click here for Directions
                </Typography>
              </Box>
              </Box>

              <Box sx={{ width: "100%", height: { xs: "100px", md: "200px" }, display: "flex", justifyContent: "center", alignItems: "center", borderRadius: 0, mt: 4 , background : isMobile ? "linear-gradient(90deg,rgba(191, 115, 44, 0.33),rgba(255, 248, 218, 0.36),rgba(197, 129, 56, 0.33))" : "none" }}>
                <Box component="img" src={timeline}  alt="2.5 BHK luxury apartment in Hyderabad by Kolla Group" sx={{ width: "auto", height: { xs: "60px", md: "130px" }, borderRadius: 2, cursor: "pointer", position: "relative",  }} />
              </Box>
            </Box>

            {/* Connectivity Details */}
            <Box sx={{ display: "flex", flexDirection: "column",justifyContent: "center", alignItems: "center", mt: 4, width: isMobile ? "100%" : "35%", px: 5 }} ref={iconsRef}>
              {!isMobile ?
              <Grid container spacing={3} sx={{ mt: 1, textAlign: "left" }}>
                {connectivityData.map((data, index) => (
                  <Grid item xs={12} key={index} sx={{ mt: 4 }}>
                    <Typography variant="h2" sx={{ fontWeight: "600", fontSize: "18px", background: "linear-gradient(90deg, #bf742c, #fff8da, #c58038)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginBottom: "8px" }}>
                      {data.category}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "12px", color: "#f5f5f5", fontWeight: "300", fontFamily: "LEMON MILK", letterSpacing: "1px" }}>
                      {data.items.map((item, idx) => (
                        <React.Fragment key={idx}>{item}<br /></React.Fragment>
                      ))}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
              :
              <Grid container spacing={3} sx={{ mt: 1, textAlign: "left",}}>
                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "flex-start" }}>
                  <Grid item xs={12} sm={6}>
                    {connectivityData.slice(0, Math.ceil(connectivityData.length / 2)).map((data, index) => (
                      <Box key={index} sx={{ mt: 4 }}>
                        <Typography
                          variant="h2"
                          sx={{
                            fontWeight: "600",
                            fontSize: "12px",
                            background: "linear-gradient(90deg, #bf742c, #fff8da, #c58038)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            marginBottom: "8px",
                          }}
                        >
                          {data.category}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "7px",
                            color: "#f5f5f5",
                            fontWeight: "300",
                            fontFamily: "LEMON MILK",
                            letterSpacing: "1px",
                          }}
                        >
                          {data.items.map((item, idx) => (
                            <React.Fragment key={idx}>{item}<br /></React.Fragment>
                          ))}
                        </Typography>
                      </Box>
                    ))}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    {connectivityData.slice(Math.ceil(connectivityData.length / 2)).map((data, index) => (
                      <Box key={index} sx={{ mt: 4 }}>
                        <Typography
                          variant="h2"
                          sx={{
                            fontWeight: "600",
                            fontSize: "12px",
                            background: "linear-gradient(90deg, #bf742c, #fff8da, #c58038)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            marginBottom: "8px",
                          }}
                        >
                          {data.category}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "7px",
                            color: "#f5f5f5",
                            fontWeight: "300",
                            fontFamily: "LEMON MILK",
                            letterSpacing: "1px",
                          }}
                        >
                          {data.items.map((item, idx) => (
                            <React.Fragment key={idx}>{item}<br /></React.Fragment>
                          ))}
                        </Typography>
                      </Box>
                    ))}
                  </Grid>
                </Box>
              </Grid>

              }
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Lightbox */}
      {isOpen && (
        <Lightbox mainSrc={images[photoIndex]} onCloseRequest={() => setIsOpen(false)} />
      )}
    </Box>
  );
};

export default ConnectedLiving;